// Infrastructure services (Domain 05): DHCP relay, first-hop redundancy
// (HSRP / VRRP / GLBP), NTP, Syslog and SNMP.
//
// Behavioural models, consistent with the rest of the simulator:
//   * DHCP relay — a client broadcast that reaches a router interface with
//     `ip helper-address` is relayed (unicast) to the server; the server picks
//     the pool whose network contains the relay address (giaddr) and the offer
//     must be routable back to the relay.
//   * FHRP — routers on the same segment with the same protocol and group
//     elect an Active/Master (priority, then highest IP; preempt rules apply).
//     The elected router answers ARP for the virtual IP. GLBP: the AVG hands
//     out the virtual MACs of every forwarder, so different hosts use different
//     routers. No hello timers: failover is immediate.
//   * NTP — clients synchronise to a reachable server that is itself a time
//     source (an NTP server host or a synchronised router); stratum = server+1.
//   * Syslog / SNMP — messages are stored on the device with its own clock and
//     delivered to configured servers that are reachable.

import { clone } from './model.js';
import { signature } from './routing.js';
import { l2Flood } from './l2.js';
import { roundTrip, l3Addr, ifStatus, hostIp, isHost } from './sim.js';
import { ipToInt, inSubnet } from './addr.js';

// ------------------------------------------------------------------ FHRP
export const FHRP = ['hsrp', 'vrrp', 'glbp'];
const fhrpCfg = (i) => (Array.isArray(i?.fhrp) ? i.fhrp : []);
const routerIfs = (net) => {
  const out = [];
  for (const [id, d] of Object.entries(net.devices)) {
    if (d.kind !== 'router') continue;
    for (const [n, i] of Object.entries(d.interfaces || {})) for (const g of fhrpCfg(i)) out.push({ id, ifc: n, i, g });
  }
  return out;
};
const memKey = (seg, proto, group) => `${seg}|${proto}|${group}`;

/**
 * FHRP election for every segment. Result keys `${dev}|${ifc}|${proto}|${group}`:
 *   { state:'Active'|'Standby'|'Listen'|'Master'|'Backup'|'Init', vip, priority, preempt,
 *     active:{dev,ifc,ip}, standby:{dev,ifc,ip}|null, members:[...], avg (glbp) }
 * Mutates net.fhrpMem (volatile) to remember who is Active — that is what makes
 * "no preempt" behave like a real router after a failure and recovery.
 */
const memoFhrp = new WeakMap();
export function fhrpState(net) {
  const all = routerIfs(net);
  const res = {};
  if (!all.length) return res;
  net.fhrpMem ||= {};
  const sig = signature(net) + JSON.stringify(net.fhrpMem);
  const m = memoFhrp.get(net);
  if (m && m.sig === sig) return m.res;
  const out = computeFhrp(net, all, res);
  memoFhrp.set(net, { sig: signature(net) + JSON.stringify(net.fhrpMem), res: out });
  return out;
}
function computeFhrp(net, all, res) {
  const seen = new Set();
  for (const a of all) {
    const k0 = `${a.id}|${a.ifc}|${a.g.proto}|${a.g.group}`;
    if (seen.has(k0)) continue;
    // members: same protocol + group on the same broadcast domain
    const reached = l2Flood(net, a.id, a.ifc).map((r) => `${r.dev}|${r.ifc}`);
    const members = all.filter((b) => b.g.proto === a.g.proto && String(b.g.group) === String(a.g.group) && (b.id === a.id && b.ifc === a.ifc || reached.includes(`${b.id}|${b.ifc}`)));
    members.forEach((b) => seen.add(`${b.id}|${b.ifc}|${b.g.proto}|${b.g.group}`));
    // stable segment identity (survives a member going down): the configured subnet
    const segOf = (b) => (b.i.ip ? `${ipToInt(b.i.ip.ip) & ipToInt(b.i.ip.mask)}/${b.i.ip.mask}` : `${b.id}:${b.ifc}`);
    const seg = segOf(a);
    const up = members.filter((b) => ifStatus(net, b.id, b.ifc).protocol === 'up' && l3Addr(net, b.id, b.ifc));
    const ipOf = (b) => l3Addr(net, b.id, b.ifc)?.ip || '0.0.0.0';
    const prio = (b) => (b.g.proto === 'vrrp' && b.g.vip && b.g.vip === ipOf(b) ? 255 : b.g.priority ?? 100);
    const rank = (x, y) => prio(y) - prio(x) || ipToInt(ipOf(y)) - ipToInt(ipOf(x));
    const sorted = up.slice().sort(rank);
    const preempts = (b) => (b.g.proto === 'hsrp' ? !!b.g.preempt : b.g.preempt !== false);
    const mk = memKey(seg, a.g.proto, a.g.group);
    let active = sorted[0] || null;
    const remembered = up.find((b) => `${b.id}|${b.ifc}` === net.fhrpMem[mk]);
    if (remembered && active && remembered !== active && !preempts(active)) active = remembered;
    if (active) net.fhrpMem[mk] = `${active.id}|${active.ifc}`;
    const standby = sorted.find((b) => b !== active) || null;
    const vip = active ? (active.g.vip || members.find((b) => b.g.vip)?.g.vip || null) : null;
    const avg = a.g.proto === 'glbp' ? active : null;
    for (const b of members) {
      const k = `${b.id}|${b.ifc}|${b.g.proto}|${b.g.group}`;
      const isUp = up.includes(b);
      let state = 'Init';
      if (isUp) {
        if (b.g.proto === 'vrrp') state = b === active ? 'Master' : 'Backup';
        else state = b === active ? 'Active' : b === standby ? 'Standby' : 'Listen';
      }
      res[k] = {
        dev: b.id, ifc: b.ifc, proto: b.g.proto, group: b.g.group, state, vip, configuredVip: b.g.vip || null,
        priority: prio(b), preempt: preempts(b), ip: ipOf(b),
        active: active ? { dev: active.id, ifc: active.ifc, ip: ipOf(active), priority: prio(active) } : null,
        standby: standby ? { dev: standby.id, ifc: standby.ifc, ip: ipOf(standby), priority: prio(standby) } : null,
        forwarders: b.g.proto === 'glbp' ? sorted.map((f, n) => ({ dev: f.id, ifc: f.ifc, ip: ipOf(f), n: n + 1 })) : null,
        avg: avg ? { dev: avg.id, ip: ipOf(avg) } : null,
        vipConflict: !!(b.g.vip && vip && b.g.vip !== vip),
      };
    }
  }
  return res;
}

/** Does (dev, ifc) answer ARP for `ip` (a virtual IP) when asked by `fromMac`? */
export function fhrpOwns(net, dev, ifc, ip, fromMac) {
  const i = net.devices[dev]?.interfaces?.[ifc];
  if (!fhrpCfg(i).length) return false;
  const st = fhrpState(net);
  for (const g of fhrpCfg(i)) {
    const s = st[`${dev}|${ifc}|${g.proto}|${g.group}`];
    if (!s || s.vip !== ip) continue;
    if (s.proto === 'glbp') {
      const fw = s.forwarders || [];
      if (!fw.length) continue;
      const h = parseInt(String(fromMac || '0').replace(/[^0-9a-f]/gi, '').slice(-4), 16) || 0;
      const pickF = fw[h % fw.length];
      if (pickF.dev === dev && pickF.ifc === ifc) return true;
      continue;
    }
    if (s.state === 'Active' || s.state === 'Master') return true;
  }
  return false;
}
/** Virtual IPs a router currently owns (for ping to the VIP). */
export function fhrpOwnedIps(net, dev) {
  const out = [];
  for (const s of Object.values(fhrpState(net))) if (s.dev === dev && (s.state === 'Active' || s.state === 'Master' || (s.proto === 'glbp' && s.avg?.dev === dev)) && s.vip) out.push(s.vip);
  return out;
}

// ------------------------------------------------------------------ DHCP relay
/**
 * Relay a DHCP request that arrived on (relayDev, relayIfc). Returns
 * { server:{dev, ip}, pool, giaddr } or null.
 */
export function relayTarget(net, relayDev, relayIfc) {
  const i = net.devices[relayDev]?.interfaces?.[relayIfc];
  if (!i?.helper) return null;
  const gi = l3Addr(net, relayDev, relayIfc);
  if (!gi) return null;
  const n = clone(net);
  const there = roundTrip(n, relayDev, i.helper, { proto: 'icmp' });
  if (!there.ok) return { error: 'server-unreachable', giaddr: gi.ip };
  const sd = net.devices[there.fwd.at];
  if (sd.kind !== 'router' || !sd.dhcp) return { error: 'no-server', giaddr: gi.ip };
  // the server must reach the relay address (giaddr) to send the offer back
  const back = roundTrip(clone(net), there.fwd.at, gi.ip, { proto: 'icmp' });
  if (!back.ok) return { error: 'no-return', giaddr: gi.ip };
  return { server: { dev: there.fwd.at, ip: i.helper }, giaddr: gi.ip };
}

// ------------------------------------------------------------------ NTP
export const LAB_NOW = Date.UTC(2026, 9, 7, 1, 14, 7, 221); // 09:14:07.221 PHT, 7 Oct 2026
const memoNtp = new WeakMap();
/**
 * NTP synchronisation for every device:
 *   { id: { synced, stratum, refId, server, master, reachable:{ip:bool} } }
 * NTP server hosts: device.ntpServer = { stratum, refId }.
 * Routers: d.ntp = { servers:[ip], master: stratum|null }.
 */
export function ntpState(net) {
  const sig = signature(net);
  const m = memoNtp.get(net);
  if (m && m.sig === sig) return m.res;
  const res = {};
  for (const [id, d] of Object.entries(net.devices)) {
    if (isHost(d) && d.ntpServer) res[id] = { synced: true, stratum: d.ntpServer.stratum || 1, refId: d.ntpServer.refId || '.GPS.', server: null, master: true, reachable: {} };
    else if (d.kind === 'router') res[id] = { synced: !!d.ntp?.master, stratum: d.ntp?.master || 16, refId: d.ntp?.master ? '.LOCL.' : '.INIT.', server: null, master: !!d.ntp?.master, reachable: {} };
  }
  const owner = (ip) => Object.entries(net.devices).find(([id, d]) => (isHost(d) ? hostIp(d)?.ip === ip : Object.keys(d.interfaces || {}).some((n) => l3Addr(net, id, n)?.ip === ip)))?.[0];
  const reach = {};
  const canReach = (a, ip) => (reach[`${a}|${ip}`] ??= roundTrip(clone(net), a, ip, { proto: 'icmp' }).ok);
  for (let round = 0; round < 6; round++) {
    let changed = false;
    for (const [id, d] of Object.entries(net.devices)) {
      if (d.kind !== 'router' || !d.ntp?.servers?.length) continue;
      let best = null;
      for (const ip of d.ntp.servers) {
        const ok = canReach(id, ip);
        res[id].reachable[ip] = ok;
        const o = owner(ip);
        const src = o && res[o];
        if (!ok || !src?.synced || src.stratum >= 15) continue;
        if (!best || src.stratum < best.stratum) best = { ip, stratum: src.stratum, dev: o };
      }
      const prev = res[id];
      const next = best ? { synced: true, stratum: best.stratum + 1, refId: best.ip, server: best.ip } : (d.ntp.master ? { synced: true, stratum: d.ntp.master, refId: '.LOCL.', server: null } : { synced: false, stratum: 16, refId: '.INIT.', server: null });
      if (prev.synced !== next.synced || prev.stratum !== next.stratum || prev.server !== next.server) { Object.assign(prev, next); changed = true; }
    }
    if (!changed) break;
  }
  memoNtp.set(net, { sig, res });
  return res;
}
/** Device time (ms): synchronised → lab time; otherwise its own drifting clock. */
export function deviceTime(net, id) {
  const d = net.devices[id];
  const st = ntpState(net)[id];
  const elapsed = (net.ticks || 0) * 7000; // lab time advances ~7 s per console command
  if (st?.synced) return LAB_NOW + elapsed;
  return LAB_NOW + elapsed + (d?.clockSkewMs || 0);
}
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** IOS `show clock` line. '*' = not authoritative (no NTP sync). */
export function clockLine(net, id) {
  const d = net.devices[id];
  if (!d?.ntp && !d?.clockSkewMs) return '*09:14:07.221 PHT Wed Sep 30 2026'; // legacy labs: unchanged
  const t = new Date(deviceTime(net, id) + 8 * 3600e3);
  const p = (n, w = 2) => String(n).padStart(w, '0');
  const synced = ntpState(net)[id]?.synced;
  return `${synced ? '' : '*'}${p(t.getUTCHours())}:${p(t.getUTCMinutes())}:${p(t.getUTCSeconds())}.${p(t.getUTCMilliseconds(), 3)} PHT ${DOW[t.getUTCDay()]} ${MON[t.getUTCMonth()]} ${t.getUTCDate()} ${t.getUTCFullYear()}`;
}
/** Syslog timestamp ("service timestamps log datetime msec"). */
export function logStamp(net, id, offsetMs = 0) {
  const t = new Date(deviceTime(net, id) + offsetMs + 8 * 3600e3);
  const p = (n, w = 2) => String(n).padStart(w, '0');
  return `${ntpState(net)[id]?.synced ? '' : '*'}${MON[t.getUTCMonth()]} ${String(t.getUTCDate()).padStart(2)} ${t.getUTCFullYear()} ${p(t.getUTCHours())}:${p(t.getUTCMinutes())}:${p(t.getUTCSeconds())}.${p(t.getUTCMilliseconds(), 3)}`;
}

// ------------------------------------------------------------------ Syslog / SNMP
export const SEVERITY = ['emergencies', 'alerts', 'critical', 'errors', 'warnings', 'notifications', 'informational', 'debugging'];
export const sevOf = (text) => { const m = String(text).match(/%[\w-]+-(\d)-/); return m ? +m[1] : 6; };
/** Store a log message on a router (also used by labs to inject events). */
export function logEvent(net, id, text, offsetMs = 0) {
  const d = net.devices[id];
  if (!d || (d.kind !== 'router' && d.kind !== 'switch')) return;
  d.logs ||= [];
  // `to`: the syslog servers configured when the message was generated (only those receive it);
  // `traps`: the SNMP trap receivers at that moment (traps enabled, host with a defined community)
  // (a message above the trap level of that moment goes to no syslog server)
  const sev = sevOf(text);
  const S = d.snmp;
  const traps = S?.traps ? (S.hosts || []).filter((h) => S.communities?.[h.community]).map((h) => h.ip) : [];
  const to = sev <= levelNum(d.logging?.trap ?? 'informational') ? [...(d.logging?.hosts || [])] : [];
  d.logs.push({ t: logStamp(net, id, offsetMs), text, sev, to, traps, sent: true });
  if (d.logs.length > 120) d.logs.shift();
}
const levelNum = (l) => (l == null ? 6 : /^\d$/.test(String(l)) ? +l : Math.max(0, SEVERITY.indexOf(l)));
/**
 * What a monitoring server at `monIp` receives:
 *   { syslog:[{dev, t, text, sev}], traps:[{dev, t, text}], polled:{dev:{ok, reason}} }
 * `pollCommunity` = the community string the NMS polls with.
 */
export function monitoringView(net, monIp, pollCommunity) {
  const out = { syslog: [], traps: [], polled: {} };
  const monId = Object.entries(net.devices).find(([, d]) => isHost(d) && hostIp(d)?.ip === monIp)?.[0];
  for (const [id, d] of Object.entries(net.devices)) {
    if (d.kind !== 'router' || d.managed === false) continue;
    const reach = monId ? roundTrip(clone(net), id, monIp, { proto: 'icmp' }).ok : false;
    const L = d.logging || {};
    if ((L.hosts || []).includes(monIp) && reach) {
      const lvl = levelNum(L.trap ?? 'informational');
      // `sent` entries were filtered (level + destination) when created; older entries use today's level
      for (const e of [...(d.sentLogs || []), ...(d.logs || [])]) if ((e.sent ? e.to.includes(monIp) : e.sev <= lvl && (!e.to || e.to.includes(monIp)))) out.syslog.push({ dev: d.hostname, t: e.t, text: e.text, sev: e.sev });
    }
    const S = d.snmp || {};
    const hostCfg = (S.hosts || []).find((x) => x.ip === monIp);
    // a trap is sent when the event happens: only to receivers configured at that moment (`e.traps`)
    const trapTo = (e) => (e.traps ? e.traps.includes(monIp) : !!(S.traps && hostCfg && S.communities?.[hostCfg.community]));
    if (reach) {
      for (const e of d.logs || []) if (/%LINK-3-UPDOWN|%LINEPROTO-5-UPDOWN/.test(e.text) && trapTo(e)) out.traps.push({ dev: d.hostname, t: e.t, text: /down/.test(e.text) ? `linkDown ${e.text.match(/Interface ([\w/.]+)/)?.[1] || ''}` : `linkUp ${e.text.match(/Interface ([\w/.]+)/)?.[1] || ''}` });
    }
    const back = monId ? roundTrip(clone(net), monId, Object.keys(d.interfaces).map((n) => l3Addr(net, id, n)?.ip).find(Boolean) || '0.0.0.0', { proto: 'icmp' }).ok : false;
    const comm = S.communities?.[pollCommunity];
    out.polled[d.hostname] = !back ? { ok: false, reason: 'unreachable' } : !comm ? { ok: false, reason: 'no response (community)' } : { ok: true, reason: 'ok' };
  }
  out.syslog.sort((a, b) => (a.t > b.t ? 1 : -1));
  return out;
}
export { inSubnet };
